import { type ActivationBatchDetail, type ActivationBatchListItem, PAGE_SIZE_DEFAULT, formatAccreditationNo } from "@seagames/contracts";
import { type ColumnDef, DataTable, IconButton, Icons, KeysetPager, Modal, ModalActions, useUiStrings } from "@seagames/ui";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute, redirect } from "@tanstack/react-router";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { DownloadIcon } from "../../components/accreditation/icons";
import { formatDateOnly } from "../../components/accreditation/model";
import { LoadError } from "../../components/e-accreditation/bulk-sheets";
import { BATCH_PAGE_SIZES, toCsv } from "../../components/e-accreditation/model";
import { ActivationResultChip, ActivationResultGuide, CardStateChip, ResultFigure, activationResultOf } from "../../components/e-accreditation/parts";
import { saveBlob } from "../../lib/api/accreditations";
import { activationBatchOptions, activationBatchesOptions } from "../../lib/api/e-accreditation";
import { useDefaultGamesId } from "../../lib/competition/games";
import { canSeeActivationHistory } from "../../lib/e-accreditation/access";
import { formatDateTime, formatShort } from "../../lib/format/datetime";
import { keysetSearch } from "../../lib/list-page/list-search";
import { useListPage } from "../../lib/list-page/use-list-page";

/**
 * Activation history (her `activationBatches`, admin.js:1695): every bulk
 * activation, newest first (keyset) — label `AC-<USER>-yyyymmdd-nnn`, when,
 * by whom, activated / skipped, valid until. Host only (the NOC never sees
 * this tab). A row opens her detail sheet: the three numbers, the cards it
 * activated, Export CSV.
 */
export const Route = createFileRoute("/_app/accreditation/e-accreditation/history")({
	validateSearch: keysetSearch({ games_id: z.string().uuid() }),
	beforeLoad: ({ context }) => {
		if (!canSeeActivationHistory(context.session)) throw redirect({ to: "/accreditation/e-accreditation" });
	},
	component: HistoryPage,
});

type Key = "label" | "when" | "result" | "activated" | "skipped" | "valid_until";

function HistoryPage() {
	const { t } = useTranslation("eAccreditation");
	const { formatNumber: fmt } = useUiStrings();
	const search = Route.useSearch();
	const navigate = Route.useNavigate();
	const gamesId = useDefaultGamesId(search.games_id);
	const [open, setOpen] = useState<ActivationBatchListItem | null>(null);
	const page = useListPage({
		paging: "keyset",
		search,
		navigate,
		listOptions: (q) => activationBatchesOptions({ games_id: gamesId ?? "", ...q }),
		columnsKey: "e-accreditation-activation-history",
		pageSizes: BATCH_PAGE_SIZES,
		defaultPageSize: PAGE_SIZE_DEFAULT,
		summary: {
			showing: (v) => t("history.pager.showing", v),
			showingNoTotal: (v) => t("history.pager.showingNoTotal", v),
			empty: t("history.pager.empty"),
		},
	});

	const columns: ColumnDef<ActivationBatchListItem, Key>[] = [
		{
			key: "label",
			header: t("history.table.label"),
			lockVisible: true,
			minWidth: "14rem",
			cell: (b) => (
				<button type="button" onClick={() => setOpen(b)} className="cursor-pointer text-left font-semibold whitespace-nowrap text-gms-navy-ink tabular-nums hover:text-gms-navy">
					{b.label}
				</button>
			),
		},
		{
			key: "when",
			header: t("history.table.when"),
			minWidth: "10rem",
			cell: (b) => (
				<span className="grid">
					<time dateTime={b.created_at} title={formatDateTime(b.created_at)} className="whitespace-nowrap text-gms-ink-2 tabular-nums">
						{formatShort(b.created_at)}
					</time>
					{b.created_by && <span className="text-[0.75rem] text-gms-ink-3">{t("common.byName", { name: b.created_by.name })}</span>}
				</span>
			),
		},
		{ key: "result", header: t("history.table.result"), minWidth: "7rem", cell: (b) => <ActivationResultChip result={activationResultOf(b.skipped_count)} /> },
		{
			key: "activated",
			header: t("history.table.activated"),
			numeric: true,
			minWidth: "6rem",
			cell: (b) => t("history.ofTotal", { n: fmt(b.activated_count), total: fmt(b.activated_count + b.skipped_count) }),
		},
		{ key: "skipped", header: t("history.table.skipped"), numeric: true, minWidth: "5rem", cell: (b) => fmt(b.skipped_count) },
		{ key: "valid_until", header: t("history.table.validUntil"), minWidth: "7rem", cell: (b) => <span className="whitespace-nowrap tabular-nums">{formatDateOnly(b.valid_until)}</span> },
	];

	return (
		<div className="flex min-w-0 flex-col gap-[1rem]">
			<div className="flex flex-wrap items-center justify-between gap-[0.875rem]">
				<p className="max-w-[40rem] text-[0.8125rem] text-gms-ink-3">{t("history.intro")}</p>
				<ActivationResultGuide size="sm" />
			</div>
			<DataTable<ActivationBatchListItem, Key>
				{...page.table}
				variant="list"
				columns={columns}
				rowKey={(b) => b.activation_batch_id}
				pager={(p) => (page.list.isError || (p === "bottom" && page.rows.length === 0) ? null : <KeysetPager {...page.pager} position={p} />)}
				rowActions={(b) => <IconButton label={t("history.row.open", { name: b.label })} icon={<Icons.Eye />} variant="row" onClick={() => setOpen(b)} />}
				empty={{ title: t("history.empty.title"), description: t("history.empty.description") }}
			/>
			{open && <ActivationSheet batch={open} onClose={() => setOpen(null)} />}
		</div>
	);
}

/**
 * Her activation detail (admin.js:1705): Activated / Skipped / Valid until,
 * the activated cards the viewer can see (`GET /activation-batches/:id`) and
 * Export CSV of them. Skipped cards are not stored — they were listed when
 * the activation ran.
 */
/** The card number as the GMS shows it: an activated card is approved, so its accreditation number (`formatAccreditationNo`); a VOID one its registration number. */
const cardNoOf = (c: ActivationBatchDetail["cards"][number]): string =>
	c.card_state === "VOID" ? c.registration_no_display : formatAccreditationNo(c.organization.code, c.registration_no);

function ActivationSheet({ batch, onClose }: { batch: ActivationBatchListItem; onClose: () => void }) {
	const { t } = useTranslation("eAccreditation");
	const { formatNumber: fmt } = useUiStrings();
	const detail = useQuery(activationBatchOptions(batch.activation_batch_id));
	const cards = detail.data?.cards ?? [];
	const exportCsv = () => {
		const csv = toCsv(
			[t("history.csv.batch"), t("cards.table.cardNo"), t("cards.table.name"), t("cards.table.organisation"), t("history.table.validUntil")],
			cards.map((c) => [batch.label, cardNoOf(c), c.printed_name, c.organization.code ?? c.organization.name, batch.valid_until]),
		);
		saveBlob(new Blob([csv], { type: "text/csv;charset=utf-8" }), `${batch.label}.csv`);
	};
	return (
		<Modal
			open
			onClose={onClose}
			size="md"
			title={batch.label}
			description={batch.created_by ? t("history.detailSubtitle", { when: formatShort(batch.created_at), name: batch.created_by.name }) : formatShort(batch.created_at)}
			footer={
				<ModalActions
					start={
						<button type="button" className="btn btn-ghost btn-sm text-gms-navy" onClick={exportCsv} disabled={cards.length === 0}>
							<DownloadIcon className="size-[0.9375rem]" />
							{t("history.exportCsv")}
						</button>
					}
				>
					<button type="button" className="btn btn-sm border-gms-navy bg-gms-navy text-white hover:bg-gms-navy-lift" onClick={onClose}>
						{t("common.close")}
					</button>
				</ModalActions>
			}
		>
			<div className="grid gap-[0.875rem]">
				<dl className="grid grid-cols-3 overflow-hidden rounded-[0.625rem] border border-gms-line">
					<ResultFigure label={t("activate.activated")} value={fmt(batch.activated_count)} of={t("common.ofN", { n: fmt(batch.activated_count + batch.skipped_count) })} />
					<ResultFigure label={t("activate.skipped")} value={fmt(batch.skipped_count)} />
					<ResultFigure label={t("activate.validUntil")} value={formatDateOnly(batch.valid_until) ?? "—"} />
				</dl>
				<section className="grid gap-[0.375rem]">
					<h3 className="text-[0.75rem] font-bold tracking-[0.04em] text-gms-ink-3 uppercase">{t("history.activatedCards")}</h3>
					{detail.isError ? (
						<LoadError error={detail.error} onRetry={() => void detail.refetch()} />
					) : detail.isPending ? (
						<div className="skeleton h-[8rem] rounded-[0.625rem]" />
					) : cards.length === 0 ? (
						<p className="text-[0.8125rem] text-gms-ink-3">{t("history.noneVisible")}</p>
					) : (
						<ul className="max-h-[18rem] divide-y divide-gms-line overflow-y-auto rounded-[0.625rem] border border-gms-line">
							{cards.map((c) => (
								<li key={c.ac_card_id} className="grid grid-cols-[8.5rem_minmax(0,1fr)_auto] items-center gap-[0.75rem] px-[0.75rem] py-[0.5rem] text-[0.8125rem]">
									<span className="text-gms-ink-3 tabular-nums">{cardNoOf(c)}</span>
									<span className="truncate font-semibold text-gms-navy-ink uppercase">{c.printed_name}</span>
									<CardStateChip state={c.card_state} />
								</li>
							))}
						</ul>
					)}
				</section>
			</div>
		</Modal>
	);
}
